/**
 * Raw-transcript breadcrumb log.
 *
 * Deliberately temporary instrumentation: we need to see what ASR actually
 * returns in the real app (across sessions, devices and spoken languages)
 * before trusting any phonetic-repair map. Stored client-side only — no audio,
 * no upload. Ring buffer of the last N entries in localStorage.
 *
 * Inspect from the browser console: `gtamVoiceLog()` (table) or
 * `gtamVoiceLog("json")` to copy out.
 */

const KEY = "gtam.voice.transcripts.v1";
const MAX = 500;

export interface TranscriptEntry {
  /** ISO timestamp. */
  at: string;
  /** Exactly what the speech engine returned. */
  raw: string;
  /** Recognition language the engine was configured with. */
  sttLang: string;
  /** UI language at the time. */
  uiLang: string;
  /** Route the user was on. */
  route: string;
  /** What the router decided to do with it (filled in by a second call). */
  outcome?: string;
  /** Repaired text, when the Norwegian phonetic fallback rewrote it. */
  repaired?: string;
}

function read(): TranscriptEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as TranscriptEntry[]) : [];
  } catch {
    return [];
  }
}

function write(list: TranscriptEntry[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX)));
  } catch {
    /* quota / private mode — logging must never break voice */
  }
}

/** Record a raw transcript. Returns an id used to attach the outcome later. */
export function logTranscript(e: Omit<TranscriptEntry, "at">): number {
  const list = read();
  const entry: TranscriptEntry = { at: new Date().toISOString(), ...e };
  list.push(entry);
  write(list);
  console.info(
    `[gtam.voice] raw="${e.raw}" stt=${e.sttLang} ui=${e.uiLang} route=${e.route}`,
  );
  return list.length - 1;
}

/** Attach the routing decision to the most recent transcript entry. */
export function logOutcome(outcome: string, repaired?: string) {
  const list = read();
  const last = list[list.length - 1];
  if (!last) return;
  last.outcome = outcome;
  if (repaired && repaired !== last.raw) last.repaired = repaired;
  write(list);
  console.info(`[gtam.voice] → ${outcome}${repaired ? ` (repaired="${repaired}")` : ""}`);
}

/**
 * Append extra detail to the most recent entry's outcome (e.g. what a page
 * listener actually did with the event). Never overwrites the routing outcome.
 */
export function logNote(note: string) {
  const list = read();
  const last = list[list.length - 1];
  if (!last) return;
  last.outcome = last.outcome ? `${last.outcome} · ${note}` : note;
  write(list);
  console.info(`[gtam.voice] note: ${note}`);
}

/**
 * Record a raw engine lifecycle event (start / audiostart / speechstart /
 * result:interim / end / error / stop). These are what tell us WHICH step
 * breaks when a spoken phrase produces no transcript at all.
 */
export function logLifecycle(kind: string, detail?: string) {
  const list = read();
  list.push({
    at: new Date().toISOString(),
    raw: `⟨${kind}⟩${detail ? ` ${detail}` : ""}`,
    sttLang: "-",
    uiLang: "-",
    route: typeof window !== "undefined" ? window.location.pathname : "-",
    outcome: "lifecycle",
  });
  write(list);
  console.info(`[gtam.voice] lifecycle ${kind}${detail ? ` ${detail}` : ""}`);
}

export function getTranscriptLog(): TranscriptEntry[] {
  return read();
}


export function clearTranscriptLog() {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

/** Expose console helpers once, on first import in the browser. */
export function installTranscriptLogConsoleHelpers() {
  if (typeof window === "undefined") return;
  const w = window as unknown as Record<string, unknown>;
  if (w.gtamVoiceLog) return;
  w.gtamVoiceLog = (format?: string) => {
    const list = read();
    if (format === "json") return JSON.stringify(list, null, 2);
    console.table(list);
    return `${list.length} transcripts`;
  };
  w.gtamVoiceLogClear = () => { clearTranscriptLog(); return "cleared"; };
}
